import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ChartMethodContent {
  /** What the chart shows */
  what: string;
  /** How it's calculated (bullet points) */
  calculation: string[];
  /** How to read/interpret the values */
  interpretation: string[];
  /** What happens on click / drill-through */
  drillthrough: string[];
  /** Data inputs used */
  inputs: string[];
  /** Thresholds used (e.g. float bands, % limits) */
  thresholds?: string[];
  /** Caveats or limitations */
  caveats?: string[];
}

export interface ChartMethodProps {
  /** Override heading (default "Method") */
  title?: string;
  method: ChartMethodContent;
  className?: string;
}

/**
 * Collapsible methodology disclosure for focus-mode charts.
 * Shows how a chart is calculated, interpreted, and what it links to.
 */
export const ChartMethod: React.FC<ChartMethodProps> = ({
  title = 'Method',
  method,
  className,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('border-t border-border pt-3 mt-4', className)}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-left"
        aria-expanded={open}
        aria-label={open ? 'Collapse method' : 'Expand method'}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-foreground/80" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-foreground/80" aria-hidden />
        )}
        <span className="font-medium">{title}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 text-xs text-muted-foreground">
          <Section heading="What it shows" text={method.what} />
          <BulletSection heading="Calculation" items={method.calculation} />
          <BulletSection heading="Interpretation" items={method.interpretation} />
          <BulletSection heading="Drill-through" items={method.drillthrough} />
          <BulletSection heading="Data inputs" items={method.inputs} />
        </div>
      )}
    </div>
  );
};

function Section({ heading, text }: { heading: string; text: string }) {
  return (
    <div>
      <h4 className="text-xs font-medium text-foreground mb-0.5">{heading}</h4>
      <p className="leading-relaxed">{text}</p>
    </div>
  );
}

function BulletSection({ heading, items }: { heading: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-medium text-foreground mb-0.5">{heading}</h4>
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">{item}</li>
        ))}
      </ul>
    </div>
  );
}
